import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3, Loader2, Play, RefreshCw } from 'lucide-react';

import { identityAPI } from '../services/api';

const ACTIVE_STATUSES = new Set(['queued', 'running']);
const TERMINAL_STATUSES = new Set(['completed', 'partial', 'failed', 'cancelled']);

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function RunStatus({ status }) {
  const normalized = String(status || 'queued');
  const good = normalized === 'completed';
  const warning = normalized === 'partial';
  const failed = normalized === 'failed';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
      good
        ? 'bg-emerald-50 text-emerald-700'
        : warning
          ? 'bg-amber-50 text-amber-800'
          : failed
            ? 'bg-red-50 text-red-700'
            : 'bg-sky-50 text-sky-700'
    }`}>
      {normalized.replaceAll('_', ' ')}
    </span>
  );
}

export default function DirectorySyncOperations({ setup, initialRuns = [], onRefresh }) {
  const sync = setup?.sync || {};
  const [runs, setRuns] = useState(initialRuns);
  const [activeRun, setActiveRun] = useState(
    initialRuns.find((item) => ACTIVE_STATUSES.has(item.status)) || null,
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(Boolean(sync.schedule_enabled));
  const [intervalMinutes, setIntervalMinutes] = useState(sync.schedule_interval_minutes || 360);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    setRuns(initialRuns);
    setActiveRun(initialRuns.find((item) => ACTIVE_STATUSES.has(item.status)) || null);
  }, [initialRuns]);

  useEffect(() => {
    setScheduleEnabled(Boolean(sync.schedule_enabled));
    setIntervalMinutes(sync.schedule_interval_minutes || 360);
  }, [sync.schedule_enabled, sync.schedule_interval_minutes]);

  const loadRuns = async () => {
    const response = await identityAPI.getMicrosoftDirectorySyncRuns();
    const nextRuns = response.data?.results || [];
    setRuns(nextRuns);
    setActiveRun(nextRuns.find((item) => ACTIVE_STATUSES.has(item.status)) || null);
    return nextRuns;
  };

  useEffect(() => {
    if (!activeRun?.id) return undefined;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const response = await identityAPI.getMicrosoftDirectorySyncRun(activeRun.id);
        if (cancelled) return;
        const next = response.data;
        setActiveRun(ACTIVE_STATUSES.has(next.status) ? next : null);
        setRuns((current) => [next, ...current.filter((item) => item.id !== next.id)].slice(0, 20));
        if (TERMINAL_STATUSES.has(next.status)) {
          window.clearInterval(timer);
          await onRefreshRef.current?.();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Failed to refresh directory sync status.');
        }
      }
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeRun?.id]);

  const saveSchedule = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await identityAPI.updateMicrosoftDirectorySyncConfig({
        mode: sync.mode || 'selected_groups',
        enabled: sync.enabled !== false,
        schedule_enabled: scheduleEnabled,
        schedule_interval_minutes: Number(intervalMinutes),
        selected_group_ids: sync.selected_group_ids || [],
      });
      setScheduleEnabled(Boolean(response.data?.schedule_enabled));
      setIntervalMinutes(response.data?.schedule_interval_minutes || intervalMinutes);
      await onRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update directory sync schedule.');
    } finally {
      setSaving(false);
    }
  };

  const triggerNow = async () => {
    setTriggering(true);
    setError('');
    try {
      const response = await identityAPI.triggerMicrosoftDirectorySync();
      setActiveRun(response.data);
      setRuns((current) => [response.data, ...current.filter((item) => item.id !== response.data.id)]);
    } catch (err) {
      const existing = err?.response?.data?.active_run;
      if (existing) {
        setActiveRun(existing);
        setRuns((current) => [existing, ...current.filter((item) => item.id !== existing.id)]);
      }
      setError(err?.response?.data?.detail || 'Failed to queue directory synchronization.');
    } finally {
      setTriggering(false);
    }
  };

  const latest = useMemo(() => runs.slice(0, 5), [runs]);
  const canTrigger = !activeRun && !triggering;

  return (
    <div className="mt-5 space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-bold text-slate-950">Recurring synchronization</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Full Microsoft directory snapshots run asynchronously. The minimum interval is one hour.
              </p>
            </div>
            <Clock3 className="h-5 w-5 text-orange-600" />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(event) => setScheduleEnabled(event.target.checked)}
              />
              Enabled
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Interval in minutes
              <input
                type="number"
                min="60"
                max="10080"
                value={intervalMinutes}
                onChange={(event) => setIntervalMinutes(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={saveSchedule}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save schedule
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Next run: {formatDate(sync.next_run_at)} · Last success: {formatDate(sync.last_success_at)}
          </p>
        </div>

        <button
          type="button"
          onClick={triggerNow}
          disabled={!canTrigger}
          className="inline-flex min-h-24 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-4 text-sm font-black text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {triggering || activeRun ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
          {triggering || activeRun ? 'Sync in progress' : 'Sync now'}
        </button>
      </div>

      {activeRun && (
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          <div className="flex items-center gap-2 font-bold">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Microsoft directory sync {activeRun.status}
          </div>
          <p className="mt-1 text-xs">Run {activeRun.id} · queued {formatDate(activeRun.queued_at)}</p>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold text-slate-950">Recent synchronization runs</h3>
          <button
            type="button"
            onClick={() => loadRuns().catch(() => setError('Failed to load sync history.'))}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Refresh synchronization history"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {latest.length === 0 && <p className="text-sm text-slate-500">No directory sync has run yet.</p>}
          {latest.map((run) => (
            <div key={run.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {run.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : run.status === 'failed' ? (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  ) : (
                    <RefreshCw className="h-4 w-4 text-sky-600" />
                  )}
                  <span className="text-sm font-bold text-slate-900">
                    {run.sync_mode?.replaceAll('_', ' ')} · {run.trigger}
                  </span>
                </div>
                <RunStatus status={run.status} />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {formatDate(run.queued_at)} · Users {run.counts?.users_seen || 0} · Groups {run.counts?.groups_seen || 0} · Directory memberships {run.counts?.memberships_seen || 0} · RBAC added {run.counts?.rbac_memberships_added || 0}
              </p>
              {run.error?.summary && <p className="mt-2 text-xs text-red-700">{run.error.summary}</p>}
              {run.status === 'failed' && !activeRun && (
                <button
                  type="button"
                  onClick={triggerNow}
                  className="mt-2 text-xs font-bold text-orange-700 hover:underline"
                >
                  Retry synchronization
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
        Missing Microsoft objects are marked stale for administrator review. This release does not remove local PII groups, disable users, or revoke sessions.
      </div>
    </div>
  );
}
